import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TriageData } from "../TriageWizard";
import { useLanguage } from "@/contexts/LanguageContext";

interface SymptomDetailsStepProps {
  data: TriageData;
  updateData: (data: TriageData) => void;
  prefilled?: boolean;
}

const SYMPTOM_KEYS = [
  { key: 'chestPain', value: 'chest_pain' },
  { key: 'shortnessOfBreath', value: 'shortness_of_breath' },
  { key: 'difficultyBreathing', value: 'difficulty_breathing' },
  { key: 'severeBleeding', value: 'severe_bleeding' },
  { key: 'lossOfConsciousness', value: 'loss_of_consciousness' },
  { key: 'confusion', value: 'confusion' },
  { key: 'speechDifficulty', value: 'speech_difficulty' },
  { key: 'fever', value: 'fever' },
  { key: 'severeHeadache', value: 'severe_headache' },
  { key: 'abdominalPain', value: 'abdominal_pain' },
  { key: 'nauseaVomiting', value: 'nausea_vomiting' },
  { key: 'dizziness', value: 'dizziness' },
  { key: 'fatigue', value: 'fatigue' },
  { key: 'backPain', value: 'back_pain' },
  { key: 'jointPain', value: 'joint_pain' },
] as const;

const ONSET_KEYS = [
  { key: 'lessThan1Hour', value: 'Less than 1 hour ago' },
  { key: 'oneToSixHours', value: '1-6 hours ago' },
  { key: 'sixTo24Hours', value: '6-24 hours ago' },
  { key: 'oneToThreeDays', value: '1-3 days ago' },
  { key: 'threeToSevenDays', value: '3-7 days ago' },
  { key: 'moreThanWeek', value: 'More than 1 week ago' },
] as const;

const SymptomDetailsStep: React.FC<SymptomDetailsStepProps> = ({ data, updateData }) => {
  const { t } = useLanguage();

  const toggleSymptom = (symptom: string) => {
    const symptoms = data.symptoms.includes(symptom)
      ? data.symptoms.filter(s => s !== symptom)
      : [...data.symptoms, symptom];
    updateData({ ...data, symptoms });
  };

  const handlePainSeverityChange = (value: number[]) => {
    updateData({ ...data, pain_severity: value[0] });
  };

  const getPainSeverityLabel = (value: number) => {
    if (value === 0) return t.symptomDetails.painLabels.noPain;
    if (value <= 3) return t.symptomDetails.painLabels.mild;
    if (value <= 6) return t.symptomDetails.painLabels.moderate;
    if (value <= 8) return t.symptomDetails.painLabels.severe;
    return t.symptomDetails.painLabels.worst;
  };

  const getPainSeverityColor = (value: number) => {
    if (value === 0) return "text-green-600";
    if (value <= 3) return "text-yellow-600";
    if (value <= 6) return "text-orange-600";
    if (value <= 8) return "text-red-600";
    return "text-red-800";
  };

  const getSymptomLabel = (key: string) => {
    return t.symptomDetails.symptoms[key as keyof typeof t.symptomDetails.symptoms] || key;
  };

  const getOnsetLabel = (key: string) => {
    return t.symptomDetails.onsetOptions[key as keyof typeof t.symptomDetails.onsetOptions] || key;
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label className="text-base font-medium">{t.symptomDetails.title}</Label>
        <div className="grid grid-cols-2 gap-2">
          {SYMPTOM_KEYS.map(({ key, value }) => (
            <Badge
              key={value}
              variant={data.symptoms.includes(value) ? "default" : "outline"}
              className="cursor-pointer hover:bg-primary/20 transition-colors justify-center p-2 text-center text-sm"
              onClick={() => toggleSymptom(value)}
            >
              {getSymptomLabel(key)}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <Label className="text-base font-medium">{t.symptomDetails.painSeverity}</Label>
        <div className="space-y-2">
          <Slider
            value={[data.pain_severity]}
            onValueChange={handlePainSeverityChange}
            max={10}
            min={0}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{t.symptomDetails.painScale.noPain}</span>
            <span className={`font-medium ${getPainSeverityColor(data.pain_severity)}`}>
              {data.pain_severity}/10 - {getPainSeverityLabel(data.pain_severity)}
            </span>
            <span>{t.symptomDetails.painScale.worstPain}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="symptom_onset" className="text-base font-medium">{t.symptomDetails.symptomOnset}</Label>
        <Select value={data.symptom_onset} onValueChange={(value) => updateData({ ...data, symptom_onset: value })}>
          <SelectTrigger>
            <SelectValue placeholder={t.symptomDetails.onsetPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {ONSET_KEYS.map(({ key, value }) => (
              <SelectItem key={value} value={value}>{getOnsetLabel(key)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="location" className="text-base font-medium">{t.symptomDetails.locationTitle}</Label>
        <Input
          id="location"
          placeholder={t.symptomDetails.locationPlaceholder}
          value={data.location}
          onChange={(e) => updateData({ ...data, location: e.target.value })}
        />
      </div>
    </div>
  );
};

export default SymptomDetailsStep;
